/**
 * Mitarbeiter-Session (Phase 6, Feb 2026)
 *
 * AUTH-SICHERHEIT V2 · Phase 2 (Feb 2026, betreibergenehmigt): Der Auth-Token
 * wandert vom localStorage in ein httpOnly-Cookie (`employee_session_cookie`,
 * gesetzt vom Backend-Login-Endpoint). In diesem Hook liegen nur noch NICHT-
 * geheime Session-Metadaten (employee-Stammdaten, company_name) im
 * localStorage — der Token wird bewusst NICHT mehr geschrieben, damit ein
 * XSS-Zugriff auf localStorage die Session nicht mehr entkapseln kann.
 * Bearer-Header-Setzung durch den Request-Interceptor fällt in dieser Phase
 * ebenfalls weg; das Browser-Session-Cookie reist per `withCredentials`
 * automatisch mit.
 *
 * Rückwärtskompatibilität (Phase-2-Übergang, in Phase 4b abgeschlossen):
 *  - Der Login-Response enthält seit Phase 4 KEIN `token`-Feld mehr
 *    (serverseitig entfernt); die Session lebt nur im httpOnly-Cookie.
 *  - Der deprecated Export `getStoredToken()` (lieferte konstant `null`)
 *    ist in Phase 4b endgültig entfernt.
 *
 * Speicher-Vertrag:
 *   localStorage["monteur_session"] = { employee: {...} }      // KEIN token
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, client } from "@/lib/api";

const STORAGE_KEY = "monteur_session";

function readSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSession(s) {
  if (!s) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  // AUTH-SICHERHEIT V2 · Phase 2: nur Metadaten persistieren, KEIN Token.
  // Falls Aufrufer versehentlich ein token-Feld übergeben, wird es hier
  // deterministisch verworfen — Single-Point-of-Truth gegen Rückfälle.
  const metadataOnly = { employee: s.employee ?? null };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(metadataOnly));
}

const SessionContext = createContext({
  session: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

export function EmployeeSessionProvider({ children }) {
  const [session, setSession] = useState(readSession);
  const [loading, setLoading] = useState(true);

  // Beim Mount: Session gegen Backend abgleichen. Nach Phase 2 gibt es keinen
  // lokalen Token mehr — die Auth-Quelle ist ausschließlich das httpOnly-
  // Cookie. Für anonyme Besucher (kein Metadaten-Eintrag) ersparen wir uns
  // den 401-Roundtrip; nur wenn lokal Employee-Metadaten vorhanden sind,
  // fragen wir das Backend über den Cookie ab.
  useEffect(() => {
    const stored = readSession();
    if (!stored?.employee?.id) {
      Promise.resolve().then(() => setLoading(false));
      return;
    }
    let cancelled = false;
    api.getMe()
      .then((employee) => {
        if (cancelled) return;
        const next = { employee };
        writeSession(next);
        setSession(next);
      })
      .catch(() => {
        if (cancelled) return;
        writeSession(null);
        setSession(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Bfcache-Restore-Schutz (Feb 2026, betreibergenehmigt nach Forensik-Report
  // FORENSIK_BFCACHE_SESSION_LECK): Wenn der Browser diese Seite aus dem
  // Back-Forward-Cache wiederherstellt (Zurück-Button nach Login-Wechsel),
  // ist der obere useEffect-Bootstrap NICHT erneut aktiv (kein Re-Mount).
  // Ohne diesen Handler würde die veraltete React-State-Kopie der VORHERIGEN
  // Employee-Session gerendert. `event.persisted === true` markiert den
  // bfcache-Restore — wir erzwingen einen vollen Reload, damit die
  // aktuell im Cookie-Store stehende Session frisch validiert wird
  // (getMe() läuft dann regulär im Mount-Effekt).
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const login = useCallback(async (companyCode, pin) => {
    const res = await api.employeeLogin(companyCode, pin);
    // Backend setzt in derselben Response das httpOnly-Cookie; wir speichern
    // nur die Metadaten, das `token`-Feld aus dem Body ignorieren wir bewusst.
    const next = { employee: res.employee };
    writeSession(next);
    setSession(next);
    try {
      if (companyCode) localStorage.setItem("last_company_code", companyCode);
    } catch {
      /* localStorage optional */
    }
    // Neu-seit-letztem-Besuch (Juni 2026): Zähler additiv an den Aufrufer
    // durchreichen (nur für den Login-Chime; landet NICHT im localStorage,
    // writeSession() persistiert weiterhin ausschließlich die Metadaten).
    return { ...next, newAssignmentsCount: Number(res.new_assignments_count) || 0 };
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.employeeLogout();
    } catch {
      // Auch wenn der Backend-Call scheitert: lokal aufräumen ist Pflicht.
    }
    writeSession(null);
    setSession(null);
  }, []);

  const refresh = useCallback(async () => {
    const stored = readSession();
    if (!stored?.employee?.id) return null;
    try {
      const employee = await api.getMe();
      const next = { employee };
      writeSession(next);
      setSession(next);
      return next;
    } catch {
      writeSession(null);
      setSession(null);
      return null;
    }
  }, []);

  return (
    <SessionContext.Provider value={{ session, loading, login, logout, refresh }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useEmployeeSession() {
  return useContext(SessionContext);
}

// ----------------------------------------------------------------------
// AUTH-SICHERHEIT V2 · Phase 2: Der frühere Bearer-Request-Interceptor
// (setzte /me/*-Employee-Token bzw. Owner-Token als Authorization-Header)
// ist ersatzlos entfernt — Auth läuft ab jetzt über httpOnly-Cookies, die
// der Browser per `withCredentials: true` automatisch mitsendet.
//
// AUSNAHME: der anonyme LP3-Vorschau-Fluss (BLOCK 3D · R2, `_run_preview`
// in `public.py`) legt eine Owner-Session mit `tenant_id: "public-preview"`
// clientseitig ohne echten Login an — das dazugehörige Token wird bewusst
// in `owner_session.preview_token` als NICHT-geheimer Marker gespeichert und
// als Bearer-Header AUSSCHLIESSLICH für Preview-Sessions gesendet. Der
// Wechselvertrag `isRegularOwnerSession` in `ownerSessionRouting.js` kennt
// diese Preview-Sonderrolle bereits.
// ----------------------------------------------------------------------

function readPreviewOwnerToken() {
  try {
    const raw = localStorage.getItem("owner_session");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.tenant_id === "public-preview" && typeof parsed.preview_token === "string") {
      return parsed.preview_token || null;
    }
    return null;
  } catch {
    return null;
  }
}

client.interceptors.request.use((config) => {
  // Preview-Sonderpfad: reine anonyme Vorschau, kein echter Login. Der
  // Preview-Token ist per Design nicht geheim (Backend akzeptiert ihn als
  // course-gescopte Preview-Owner-Session, siehe `services/course_scope.py`).
  // Für alle anderen Sessions greift ausschließlich das httpOnly-Cookie.
  const token = readPreviewOwnerToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

client.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status !== 401) return Promise.reject(error);
    if (typeof window === "undefined") return Promise.reject(error);

    // AUTH-SICHERHEIT V2 · Phase 2: Bei 401 nicht mehr nur lokal löschen,
    // sondern zusätzlich den Logout-Endpoint aufrufen — der Server löscht
    // dann das httpOnly-Cookie serverseitig (Client kann es nicht selbst).
    // fail-safe: wenn der Logout-Call scheitert, wird lokal trotzdem
    // aufgeräumt.
    const isEmployeeRoute =
      url.startsWith("/me") ||
      url === "/auth/me" ||
      url === "/auth/employee-logout";

    if (isEmployeeRoute) {
      try { await api.employeeLogout(); } catch { /* fail-safe */ }
      writeSession(null);
      if (window.location.pathname.startsWith("/monteur")) {
        window.location.href = "/monteur/login";
      }
    } else {
      // Owner-Route (alles ausser /me/*, /auth/*, öffentliche Endpoints).
      try { await api.ownerLogout(); } catch { /* fail-safe */ }
      try { localStorage.removeItem("owner_session"); } catch { /* localStorage optional */ }
      if (window.location.pathname.startsWith("/unternehmer")) {
        window.location.href = "/unternehmer/login";
      }
    }
    return Promise.reject(error);
  }
);
